import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import offlineSyncService from '../services/offlineSyncService';
import offlinePrefetchService from '../services/offlinePrefetchService';
import settingsService from '../services/settingsService';
import offlineAttachmentService from '../services/offlineAttachmentService';
import companyBrandingAssetService from '../services/companyBrandingAssetService';
import { resolveTenantId } from '../utils/tenantIdentity';

const OfflineSyncContext = createContext(null);

const DEFAULT_OFFLINE_SETTINGS = {
  enabled: true,
  autoSync: true,
  prefetchOnLogin: true,
  historyRetentionDays: 90,
  imageRetentionDays: 30,
  keepUnsyncedUntilSynced: true,
};

export const OfflineSyncProvider = ({ children }) => {
  const { userData, permissions = [] } = useAuth();
  const tenantId = resolveTenantId(userData);
  const [status, setStatus] = useState(() => offlineSyncService.getSnapshot());
  const [offlineSettings, setOfflineSettings] = useState(DEFAULT_OFFLINE_SETTINGS);
  const [prefetch, setPrefetch] = useState({ running: false, progress: null, result: null, error: '' });
  const [attachmentPending, setAttachmentPending] = useState(0);
  const [brandingPending, setBrandingPending] = useState(0);
  const prefetchedTenantRef = React.useRef('');
  const latestPermissionsRef = React.useRef(permissions);
  latestPermissionsRef.current = permissions;

  useEffect(() => {
    let mounted = true;
    const unsubscribe = offlineSyncService.subscribe((next) => mounted && setStatus(next));
    offlineSyncService.init().catch(() => {});
    offlineAttachmentService.countPending().then(setAttachmentPending).catch(() => {});
    companyBrandingAssetService.countPending().then(setBrandingPending).catch(() => {});
    return () => { mounted = false; unsubscribe(); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    let idleHandle = 0;
    if (!tenantId) return () => { cancelled = true; };

    (async () => {
      try {
        const company = await settingsService.getCompanySettings(tenantId);
        if (cancelled) return;
        const stored = company?.operationalSettings?.offline || company?.settings?.operationalSettings?.offline || {};
        const normalized = { ...DEFAULT_OFFLINE_SETTINGS, ...(stored || {}) };
        setOfflineSettings(normalized);

        // Offline prefetch is intentionally delayed so it never competes with
        // authentication, the dashboard or the first routed page for Firestore
        // connections and main-thread time.
        if (
          normalized.enabled !== false &&
          normalized.prefetchOnLogin !== false &&
          !offlineSyncService.isOfflineLikely() &&
          prefetchedTenantRef.current !== tenantId
        ) {
          const runPrefetch = async () => {
            if (cancelled || document.visibilityState === 'hidden') return;
            prefetchedTenantRef.current = tenantId;
            setPrefetch((prev) => ({ ...prev, running: true, error: '' }));
            try {
              const result = await offlinePrefetchService.prefetch({
                tenantId,
                permissions: latestPermissionsRef.current,
                onProgress: (progress) => !cancelled && setPrefetch((prev) => ({ ...prev, progress })),
              });
              if (!cancelled) setPrefetch({ running: false, progress: null, result, error: '' });
            } catch (error) {
              prefetchedTenantRef.current = '';
              if (!cancelled) {
                setPrefetch((prev) => ({
                  ...prev,
                  running: false,
                  error: String(error?.message || error || ''),
                }));
              }
            }
          };

          timer = window.setTimeout(() => {
            if (typeof window.requestIdleCallback === 'function') {
              idleHandle = window.requestIdleCallback(runPrefetch, { timeout: 4000 });
            } else {
              runPrefetch();
            }
          }, 5000);
        }
      } catch (error) {
        if (!cancelled) {
          setPrefetch((prev) => ({
            ...prev,
            running: false,
            error: String(error?.message || error || ''),
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (idleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
    };
  }, [tenantId]);


  useEffect(() => {
    if (offlineSettings.autoSync === false) return undefined;
    const handler = () => {
      if (!offlineSyncService.isOfflineLikely()) {
        offlineSyncService.syncNow({ silent: true });
        offlineAttachmentService.processQueue().finally(() => offlineAttachmentService.countPending().then(setAttachmentPending).catch(() => {}));
        companyBrandingAssetService.processQueue().finally(() => companyBrandingAssetService.countPending().then(setBrandingPending).catch(() => {}));
      }
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [offlineSettings.autoSync]);

  const syncNow = useCallback(async () => {
    const result = await offlineSyncService.syncNow();
    if (!offlineSyncService.isOfflineLikely()) {
      await offlineAttachmentService.processQueue().catch(() => {});
      await companyBrandingAssetService.processQueue().catch(() => {});
      setAttachmentPending(await offlineAttachmentService.countPending().catch(() => 0));
      setBrandingPending(await companyBrandingAssetService.countPending().catch(() => 0));
    }
    return result;
  }, []);
  const setForcedOffline = useCallback((enabled) => offlineSyncService.setForcedOffline(enabled), []);
  const retryFailed = useCallback(() => offlineSyncService.retryFailed(), []);
  const refreshCache = useCallback(async () => {
    if (!tenantId || offlineSyncService.isOfflineLikely()) return null;
    setPrefetch((prev) => ({ ...prev, running: true, error: '' }));
    try {
      const result = await offlinePrefetchService.prefetch({
        tenantId,
        permissions,
        onProgress: (progress) => setPrefetch((prev) => ({ ...prev, progress })),
      });
      setPrefetch({ running: false, progress: null, result, error: '' });
      return result;
    } catch (error) {
      setPrefetch((prev) => ({ ...prev, running: false, error: String(error?.message || error || '') }));
      return null;
    }
  }, [tenantId, permissions]);

  const value = useMemo(() => ({
    ...status,
    offlineSettings,
    prefetch,
    attachmentPending,
    brandingPending,
    syncNow,
    setForcedOffline,
    retryFailed,
    refreshCache,
    getMutations: () => offlineSyncService.listMutations(),
  }), [status, offlineSettings, prefetch, attachmentPending, brandingPending, syncNow, setForcedOffline, retryFailed, refreshCache]);

  return <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>;
};

export const useOfflineSync = () => {
  const value = useContext(OfflineSyncContext);
  if (!value) throw new Error('useOfflineSync must be used inside OfflineSyncProvider');
  return value;
};

export default OfflineSyncContext;
